import { LRUCache } from "lru-cache";
import { compressToBase64, decompressFromBase64 } from "lz-string";
/**
 * Compute the field name for a compressed representation of a field in an object.
 * @param fieldName Raw field name
 * @returns The field name that will be assigned by the LZ compression process.
 */
export function lzCompressedFieldName(fieldName) {
    return `${fieldName}__compressed`;
}
class LzError extends Error {
    constructor(message, cause) {
        super(message, { cause });
    }
}
/**
 * Decompress a field in place that has been compressed with the LZstring algorithm.  Inverse of the {@link lzCompressField} function.
 *
 * @param data The object whose field should be decompressed.  This object will be modified by the function.
 * @param fieldName Which field should be decompressed.  This will be suffixed with __compressed to find the compressed version of the field.
 * @param deleteCompressedDataOnSuccess If true, the compressed data will be deleted on success.
 * @remarks You should test your compression ratio on realistic data if the ratio is critical!
 * @todo better error messages
 * @todo should this throw or be a no-op if the field doesn't exist?
 */
export function lzDecompressField(data, fieldName, deleteCompressedDataOnSuccess = true) {
    const compressedFieldName = lzCompressedFieldName(fieldName);
    const compressedData = data[compressedFieldName];
    if (!compressedData || typeof compressedData !== "string") {
        throw new LzError(`Missing compressed data for ${fieldName}`);
    }
    const decompressedData = decompressFromBase64(compressedData);
    if (decompressedData) {
        data[fieldName] = JSON.parse(decompressedData);
        if (deleteCompressedDataOnSuccess)
            delete data[compressedFieldName];
    }
    else {
        throw new LzError(`Could not decompress data for ${fieldName}`);
    }
}
const lzCompressFieldCache = new LRUCache({
    // 100MB should be more than enough for all the unique
    // part_version_data_cache we are compressing. They have a max size of
    // roughly 2MB each, including the uncompressed key and the compressed
    // value. 100MB makes space for 50 unique max-sized sublayouts.
    maxSize: 1024 * 1024 * 100,
    // Rough estimate of size per item, assumes ASCII
    sizeCalculation: (value, key) => {
        return value.length + key.length;
    },
});
/**
 * Compress a field in place that has been compressed via the LZstring algorithm.
 *
 * @param data The object whose field should be compressed.  The object will be
 * modified by this function and a new field will be created on the object
 * representing the compressed data.
 * @param fieldName The name of the field to be compressed.
 * @param deleteUncompressedDataOnSuccess If true, the original field will be
 * deleted after compression.
 * @param useCache If true, the function will cache the compressed data and use
 * it if the same data is compressed again. Cache fields known to be large and
 * slow-changing.
 * @todo It's quite annoying that this modifies in place, because we don't have
 * a chance to change types of data from T to WithCompresedField<T, K>
 * QUESTION: why does this modify in place?
 */
export function lzCompressField(data, fieldName, deleteUncompressedDataOnSuccess = true, useCache = false) {
    const uncompressedData = data[fieldName];
    if (!uncompressedData) {
        throw new LzError(`Missing decompressed data for ${fieldName}`);
    }
    const json = JSON.stringify(uncompressedData);
    const cached = useCache ? lzCompressFieldCache.get(json) : undefined;
    data[lzCompressedFieldName(fieldName)] = cached ?? compressToBase64(json);
    if (deleteUncompressedDataOnSuccess) {
        delete data[fieldName];
    }
    if (useCache) {
        lzCompressFieldCache.set(json, data[lzCompressedFieldName(fieldName)]);
    }
}
//# sourceMappingURL=lz.js.map